import React, { useEffect } from 'react';
import { Autocomplete, Stack } from '@contentful/f36-components';
import { useSDK } from '@contentful/react-apps-toolkit';
import useMsalToken from '../hooks/useMsalToken';
import useAutocompleteItems from "../hooks/useAutocompleteItems";
import { Pill } from '@contentful/f36-pill';
import { config } from "../config/app-config";

const Field = () => {
    const sdk = useSDK();
    const mode = sdk.parameters.instance.mode;
    const isMulti = mode !== 'tournaments';
    const accessToken = useMsalToken();
    const [selectedItem, setSelectedItem] = React.useState(isMulti ? [] : { name: '', id: '' });
    const idField = config[sdk.parameters.installation.environment].field[sdk.parameters.instance.mode].id;
    const [tournamentId, setTournamentId] = React.useState(sdk.entry.fields.tournamentId.getValue());
    const { items } = useAutocompleteItems(accessToken);
    const [filteredItems, setFilteredItems] = React.useState(items);

    useEffect(() => {
        const detach = sdk.entry.fields.tournamentId.onValueChanged((value) => {
            setTournamentId(value);

            if (!value) {
                setSelectedItem(isMulti ? [] : { name: '', id: '' });
                setFilteredItems([]);
                sdk.entry.fields[idField].setValue(null);
                sdk.field.setValue(null);
            }
        });

        return () => {
            detach();
        };
    }, [idField, isMulti, mode, sdk.entry.fields, sdk.entry.fields.tournamentId, sdk.field]);

    const resetChildFields = () => {
        sdk.entry.fields[config[sdk.parameters.installation.environment].field.roles.id].setValue(null);
        sdk.entry.fields[config[sdk.parameters.installation.environment].field.roles.name].setValue(null);
        sdk.entry.fields[config[sdk.parameters.installation.environment].field.teams.id].setValue(null);
        sdk.entry.fields[config[sdk.parameters.installation.environment].field.teams.name].setValue(null);
    }

    useEffect(() => {
        if (!items.length) return;

        if (!isMulti) {
            const item = items.find((item) => item.name === sdk.field.getValue());
            setSelectedItem(item ? item : { name: '', id: '' });
        } else {
            const existingItems = sdk.field.getValue() ?? [];
            const selectedItems = items.filter((item) => existingItems.includes(item.name));

            const invalidItems = existingItems
              .filter((name) => !items.find((item) => item.name === name))
              .map(() => ({ name: '', id: '' }));

            setSelectedItem([...selectedItems, ...invalidItems]);
        }
        setFilteredItems(items);
    }, [items, accessToken, isMulti, sdk.field]);

    const handleInputValueChange = (value) => {
        if (!value) {
            return setFilteredItems(items);
        }

        const newFilteredItems = items.filter((item) =>
          item.name.toLowerCase().includes(value.toLowerCase()),
        );

        setFilteredItems(newFilteredItems);
    };

    const handleSelectItem = (selectedObj) => {
        if (!isMulti) {
            sdk.field.setValue(selectedObj.name);
            sdk.entry.fields[idField].setValue(selectedObj.id);
            setSelectedItem(selectedObj);

            // Reset teams and roles fields if changing tournament
            if (mode === 'tournaments') {
                resetChildFields();
            }

        } else {
            const selectedItems = [...selectedItem];
            const isAlreadySelected = selectedItems.some((item) => item.id === selectedObj.id);

            if (!isAlreadySelected) {
                selectedItems.push(selectedObj);
                setSelectedItem(selectedItems);
                sdk.field.setValue(selectedItems.map((item) => item.name));
                sdk.entry.fields[idField].setValue(selectedItems.map((item) => item.id));
            }
        }
    };

    const handleClear = () => {
        sdk.field.setValue(null);
        sdk.entry.fields[idField].setValue(isMulti ? [] : null);
        setSelectedItem({ name: '', id: '' });

        // Reset teams and roles fields if changing tournament
        if (mode === 'tournaments') {
            resetChildFields();
        }
    }

    const getPillLabel = (item) => {
        const isValid = items.find((i) => i.id === item.id);
        return isValid ? item.name : 'Invalid object';
    }

    const getPillVariant = (item) => {
        return items.find((i) => i.id === item.id) ? 'active' : 'deleted';
    }

    return (
      <Stack flexDirection="column" alignItems="start">
          <Autocomplete
            isDisabled={mode !== 'tournaments' && !tournamentId}
            items={filteredItems}
            onInputValueChange={handleInputValueChange}
            onSelectItem={handleSelectItem}
            selectedItem={selectedItem}
            itemToString={(item) => item?.name}
            showClearButton={false}
            textOnAfterSelect="clear"
            renderItem={(item) => `${item?.name}`}
          />
          <Stack>
              {isMulti && selectedItem.length ? selectedItem.map((item) => (
                <Pill
                  key={item.id}
                  testId={item.name}
                  label={getPillLabel(item)}
                  variant={getPillVariant(item)}
                  style={{border: getPillVariant(item) === 'deleted' ? '2px solid crimson' : 'inherit' }}
                  onClose={() => {
                      const updatedItems = selectedItem.filter((selectedItem) => selectedItem.id !== item.id);

                      setSelectedItem(updatedItems);
                      sdk.field.setValue(updatedItems.map((item) => item.name));
                      sdk.entry.fields[idField].setValue(updatedItems.map((item) => item.id));
                  }}
                  isDraggable={false}
                />
              )) : null}
          </Stack>
          {selectedItem.name ? <Pill
            testId={selectedItem ? selectedItem.name : ''}
            label={getPillLabel(selectedItem)}
            variant={getPillVariant(selectedItem)}
            style={{border: getPillVariant(selectedItem) === 'deleted' ? '2px solid crimson' : 'inherit' }}
            onClose={handleClear}
            isDraggable={false}
          /> : null}
      </Stack>
    );
};

export default Field;